/* ==========================================================================
   usdt-price.js — <usdt-price> companion price
   --------------------------------------------------------------------------
   The Liquid snippet already printed a correct value, so this file never
   has to run for the page to be right. It adds:
     1. the reveal choreography (roll digits, settle hover states),
     2. an optional live rate — NBU (official USD, USDT ≈ USD), CoinGecko
        or Binance — cached in localStorage; the first visitor waits for it
        (up to 2.5 s) before the reveal, so the number never jumps,
     3. continuity across variant changes — product.js swaps the price
        block via innerHTML, which re-creates the element; the previous
        digits are remembered so the odometer rolls from the old value.
   ========================================================================== */
(() => {
  if (customElements.get('usdt-price')) return;

  const THIN = ' ';
  const CACHE_KEY = 'sstories:usdt-rate';
  const TTL = { nbu: 60 * 60 * 1000, coingecko: 15 * 60 * 1000, binance: 15 * 60 * 1000 };
  const WAIT = 2500;               // how long the first paint waits for a live rate
  const previous = new Map();      // slot key → last rendered text
  let inflight = null;

  const format = (units, dec) => {
    const [whole, frac] = units.toFixed(dec).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, THIN);
    return frac ? `${grouped}.${frac}` : grouped;
  };

  const SOURCES = {
    /* National Bank of Ukraine: official UAH per USD, set once per business
       day. USDT is used as a dollar here, so the USD rate is the USDT rate. */
    nbu: async (cur) => {
      if (cur.toUpperCase() !== 'UAH') throw new Error('NBU rate is UAH only');
      const r = await fetch('https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?valcode=USD&json');
      return Number((await r.json())[0].rate);
    },
    coingecko: async (cur) => {
      const c = cur.toLowerCase();
      const r = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=tether&vs_currencies=${c}`);
      return Number((await r.json()).tether[c]);
    },
    binance: async (cur) => {
      const r = await fetch(`https://api.binance.com/api/v3/ticker/price?symbol=USDT${cur.toUpperCase()}`);
      return Number((await r.json()).price);
    }
  };

  const readCache = (source, cur) => {
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (c && c.source === source && c.cur === cur && Date.now() - c.t < (TTL[source] || TTL.binance)) return c.rate;
    } catch (e) { /* private mode etc. */ }
    return null;
  };

  /* { rate } when the cache answers synchronously, otherwise { promise }
     for the one shared fetch (resolves to the rate, or null on failure).
     Elements that already rendered are refreshed when it lands. */
  const liveRate = (source, cur) => {
    if (!SOURCES[source]) return {};
    const cached = readCache(source, cur);
    if (cached) return { rate: cached };
    if (!inflight) {
      inflight = SOURCES[source](cur)
        .then((rate) => {
          if (!(rate > 0)) throw new Error('bad rate');
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ source, cur, rate, t: Date.now() })); } catch (e) {}
          document.querySelectorAll(`usdt-price[data-source="${source}"]`).forEach((el) => el.applyRate(rate));
          return rate;
        })
        .catch(() => null)
        .finally(() => { inflight = null; });
    }
    return { promise: inflight };
  };
  const after = (ms) => new Promise((res) => setTimeout(() => res(null), ms));

  class UsdtPrice extends HTMLElement {
    connectedCallback() {
      this.num = this.querySelector('.usdt__num');
      this.sr = this.querySelector('.visually-hidden');
      if (!this.num) return;

      this.dec = Number(this.dataset.decimals) || 0;
      this.slot = this.slotKey();

      const text = this.dataset.value;
      const source = this.dataset.source;
      const live = source && source !== 'manual' ? liveRate(source, this.dataset.currency || 'UAH') : {};

      if (live.rate) {
        this.render(this.compute(live.rate), true);
      } else if (live.promise) {
        /* First visit: hold the entrance until the rate lands (or WAIT runs
           out) so the reveal shows the right number instead of swapping it. */
        this.pending = true;
        this.classList.add('is-pending');
        Promise.race([live.promise, after(WAIT)]).then((rate) => {
          this.pending = false;
          this.classList.remove('is-pending');
          if (!this.isConnected) return;
          this.render(rate ? this.compute(rate) : text, true);
          this.settleWhenDone();
        });
        return;
      } else {
        this.render(text, true);
      }
      this.settleWhenDone();
    }

    disconnectedCallback() {
      if (this.current) previous.set(this.slot, this.current);
    }

    /* Same position inside the same price block → same slot, so the digits
       remembered before product.js replaced the block carry over. */
    slotKey() {
      const box = this.closest('[id]');
      const all = box ? box.querySelectorAll('usdt-price') : [];
      return `${box ? box.id : 'page'}:${Array.prototype.indexOf.call(all, this)}`;
    }

    compute(rate) {
      this.liveRate = rate;
      const amount = Number(this.dataset.amount);
      const markup = Number(this.dataset.markup) || 0;
      return format(amount / 100 / (rate * (1 + markup / 100)), this.dec);
    }

    applyRate(rate) {
      if (this.pending) return;                 // its own connect flow will render
      const text = this.compute(rate);
      if (text !== this.current) this.render(text, false);
    }

    /* "за курсом НБУ 1 USD = 44,86 ₴" on hover or focus. Shown only where
       the snippet made the element focusable (product pages), so cards do
       not grow tooltips. The NBU wording is used only for the NBU source. */
    updateHint() {
      const rate = this.liveRate || Number(this.dataset.rate);
      const live = this.liveRate && this.dataset.source === 'nbu';
      const template = live ? this.dataset.hintLive : this.dataset.hintManual;
      if (!this.hasAttribute('tabindex') || !template || !(rate > 0)) { this.removeAttribute('data-hint'); return; }
      this.dataset.hint = template.replace('__RATE__', rate.toFixed(2).replace('.', ','));
    }

    render(text, initial) {
      const from = initial ? previous.get(this.slot) : this.current;
      this.current = text;
      this.dataset.value = text;
      this.updateHint();

      if (this.sr) {
        const prefix = this.querySelector('.usdt__prefix')?.textContent || '';
        const label = this.querySelector('.usdt__label')?.textContent || '';
        this.sr.textContent = `${prefix} ${text} ${label}`.trim();
      }

      if (this.dataset.style === 'roll') {
        this.roll(text, from);
      } else {
        this.num.textContent = text;
        if (!initial) {
          this.num.classList.remove('is-swap');
          void this.num.offsetWidth;
          this.num.classList.add('is-swap');
          this.num.addEventListener('animationend', () => this.num.classList.remove('is-swap'), { once: true });
        }
      }
    }

    /* Odometer. Digits align from the right so decimals stay put when the
       integer part grows; new columns start at 0. */
    roll(text, from) {
      const prevDigits = (from || '').replace(/\D/g, '');
      const chars = text.split('');
      const digitCount = chars.filter((c) => /\d/.test(c)).length;
      const frag = document.createDocumentFragment();
      const columns = [];
      let seen = 0;

      chars.forEach((ch) => {
        if (!/\d/.test(ch)) {
          const c = document.createElement('span');
          c.className = 'usdt__c';
          c.textContent = ch;
          frag.appendChild(c);
          return;
        }
        const fromRight = digitCount - 1 - seen;           // 0 = last digit
        const start = prevDigits[prevDigits.length - 1 - fromRight] ?? '0';
        seen += 1;

        const col = document.createElement('span');
        col.className = 'usdt__d';
        col.style.setProperty('--i', fromRight);
        col.style.setProperty('--n', start);
        const strip = document.createElement('span');
        strip.className = 'usdt__strip';
        for (let d = 0; d < 10; d += 1) {
          const s = document.createElement('span');
          s.textContent = d;
          strip.appendChild(s);
        }
        col.appendChild(strip);
        frag.appendChild(col);
        columns.push([col, ch]);
      });

      this.num.classList.add('is-roll');
      this.num.replaceChildren(frag);
      void this.num.offsetWidth;                            // commit the start position
      columns.forEach(([col, target]) => col.style.setProperty('--n', target));
    }

    /* After the entrance finishes, drop the keyframes so hover states own
       the element (pearl sheen replay, hairline erase). */
    settleWhenDone() {
      /* getAnimations() flushes style, so the CSS animations created by the
         render above are already listed — no frame needs to pass first. */
      const anims = this.getAnimations ? this.getAnimations({ subtree: true }) : [];
      if (!anims.length) { this.classList.add('is-settled'); return; }
      Promise.all(anims.map((a) => a.finished.catch(() => null)))
        .then(() => this.classList.add('is-settled'));
    }
  }

  customElements.define('usdt-price', UsdtPrice);
})();
